import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { Button, Typography, useDevice } from "design_system/src";
import { TVehicle } from "models/inventory/interfaces/vehicle.interface";
import { ReactComponent as Moto } from "design_system/src/static/icons/motorcycle.svg";
import { ReactComponent as Arrow } from "design_system/src/static/icons/up-lg.svg";
import { vehicleDetailData } from "models/inventory/interfaces/vehicle-detail.interfaces";
import { capitalize } from "helpers/capitalize";
import {
  translateBrakeSystem,
  translateCoolingSystem,
  translateEngineType,
  translateFrontSuspension,
  translateFuelSystem,
  translateRearSuspension,
  translateTransmission,
} from "views/VehicleDetail/helpers/translations";
import styles from "./Specs.module.scss";

interface Spec {
  title: String;
  text: String;
}

interface SpecGroup {
  title: String;
  icon: React.ReactElement;
  rows: Spec[];
}

interface SpecsBoxProps {
  data: SpecGroup;
}

const SpecsBox = ({ data }: SpecsBoxProps) => {
  const [active, setActive] = useState<boolean>(true);

  return (
    <Box
      display="flex"
      flexDirection="column"
      className="dso_card m_b_lg"
    >
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        className={`${styles.SpecsBox}${active ? ` ${styles["SpecsBox--active"]}` : ""}`}
        onClick={() => setActive(!active)}
      >
        <div className="display_flex flex_align_center">
          {data.icon}
          <Typography
            scale="large"
            weight="600"
            className="m_l_md"
          >
            {data.title}
          </Typography>
        </div>

        <div>
          <Arrow className={`text_primary_300 w_lg ${styles.SpecsArrow}${active ? ` ${styles["SpecsArrow--active"]}` : ""}`}/>
        </div>
      </Box>

      {active && (
        <Box
          display="flex"
          flexDirection="column"
          className={styles.SpecsBoxSub}
        >
          {data.rows.map((item, index) => {
            return (
              <div className={`${styles.SpecsBoxItemRow}${data.rows.length === index + 1 ? ` ${styles["SpecsBoxItemRow--no-border"]}` :""}`} key={`spec-item-${index}`}>
                <div className={styles.SpecsBoxItem}>
                  <Typography
                    scale="medium"
                    weight="600"
                    className={styles.SpecsBoxItemText}
                  >
                    {item.title}
                  </Typography>
                </div>
                <div className={styles.SpecsBoxItem}>
                  <Typography
                    scale="medium"
                    weight="400"
                    className={styles.SpecsBoxItemText}
                  >
                    {item.text.includes("undefined") ? "" : item.text}
                  </Typography>
                </div>
              </div>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

interface SpecsProps {
  vehicle?: TVehicle;
  vehicleDetails?: vehicleDetailData;
  fullWidth: boolean;
}

const Specs = ({ vehicle, vehicleDetails, fullWidth }: SpecsProps) => {
  const [technicalSheet, setTechnicalSheet] = useState<string | null>(null);
  const [specsInfo, setSpecsInfo] = useState<SpecGroup[]>([]);
  const { isMobile } = useDevice();

  useEffect(() => {
    if (vehicle?.documents && vehicle.documents.length > 0) {
      setTechnicalSheet(vehicle.documents[0].url);
    }
  }, [vehicle]);

  useEffect(() => {
    if (vehicleDetails) {
      setSpecsInfo([
        {
          title: "Motor y Transmisión",
          icon: <Moto className="text_primary_300 w_lg" />,
          rows: [
            {
              title: "Tipo de motor",
              text: capitalize(translateEngineType(vehicleDetails.engineType))
            },
            {
              title: "Cilindraje",
              text: `${vehicle?.cylindersCapacity} cc`
            },
            {
              title: "Velocidad máxima",
              text: `${vehicleDetails.maxSpeedKmh} Km/h`
            },
            {
              title: "Potencia máxima",
              text: vehicleDetails.maxPower
            },
            {
              title: "Torque máximo",
              text: vehicleDetails.maxTorque ?? ""
            },
            {
              title: "Rendimiento de combustible",
              text: `${vehicleDetails.fuelEfficiencyKml} Km/l`
            },
            {
              title: "Sistema de alimentación",
              text: capitalize(translateFuelSystem(vehicleDetails.fuelSystem ?? ""))
            },
            {
              title: "Capacidad de combustible",
              text: `${vehicleDetails.fuelCapacityLiters} Litros`
            },
            {
              title: "Transmisión",
              text: `${vehicleDetails.transmissionSpeeds} velocidades, ${capitalize(translateTransmission(vehicleDetails.transmissionType))}`
            },
            {
              title: "Sistema de enfriamiento",
              text: capitalize(translateCoolingSystem(vehicleDetails.coolingSystem))
            },
          ]
        },
        {
          title: "Chasis y Suspensión",
          icon: <Moto className="text_primary_300 w_lg" />,
          rows: [
            {
              title: "Suspensión delantera",
              text: capitalize(translateFrontSuspension(vehicleDetails.frontSuspension))
            },
            {
              title: "Suspensión trasera",
              text: capitalize(translateRearSuspension(vehicleDetails.rearSuspension))
            },
            {
              title: "Frenos delanteros",
              text: capitalize(translateBrakeSystem(vehicleDetails.frontBrakes))
            },
            {
              title: "Frenos traseros",
              text: capitalize(translateBrakeSystem(vehicleDetails.rearBrakes))
            },
            {
              title: "Llanta delantera",
              text: vehicleDetails.frontTire
            },
            {
              title: "Llanta trasera",
              text: vehicleDetails.rearTire
            }
          ]
        },
        {
          title: "Dimensiones y Peso",
          icon: <Moto className="text_primary_300 w_lg" />,
          rows: [
            {
              title: "Longitud total",
              text: `${vehicleDetails.dimensions.lengthMm} mm`
            },
            {
              title: "Ancho total",
              text: `${vehicleDetails.dimensions.widthMm} mm`
            },
            {
              title: "Altura total",
              text: `${vehicleDetails.dimensions.heightMm} mm`
            },
            {
              title: "Distancia entre ejes",
              text: `${vehicleDetails.dimensions.wheelbaseMm} mm`
            },
            {
              title: "Altura del asiento",
              text: `${vehicleDetails.dimensions.seatHeightMm} mm`
            },
            {
              title: "Capacidad de carga",
              text: `${vehicleDetails.dimensions.loadCapacityKg} Kg`
            }
          ]
        },
      ]);
    }
  }, [vehicleDetails]);

  const handleDownload = async () => {
    if (technicalSheet) {
      try {
        const response = await fetch(technicalSheet);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `ficha-tecnica-${vehicle?.brand.name}-${vehicle?.model.name}.pdf`; // Asumiendo que es un PDF
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error al descargar el archivo:", error);
      }
    }
  };

  if (!vehicle || !vehicleDetails) return null;

  return (
    <div>
      <Typography
        scale="large"
        weight="600"
        className="m_t_none m_b_sm"
        component="h2"
      >
        Especificaciones
      </Typography>
      <div className="display_flex flex_wrap w_100_per">
        {specsInfo.map((spec, index) => {
          return (
            <div
              key={`spec-box-${index}`}
              className={fullWidth || isMobile? "w_100_per": `w_50_per${index % 2 === 0 ? " p_r_sm" : " p_l_sm"}`}
            >
              <SpecsBox data={spec}/>
            </div>
          );
        })}
      </div>
      <div className={fullWidth || isMobile? "w_100_per": "w_50_per p_r_sm"}>
        <Button
          className="w_100_per"
          scale="small"
          onClick={handleDownload}
          disabled={!technicalSheet}
        >
          <Typography scale="medium" weight="600" className="display_flex flex_align_center">
            Descargar ficha técnica
            <div className="m_l_md h_lg">
              <Arrow
                className={`w_lg ${!technicalSheet ? "text_neutral_600" : "text_neutral_100"}`}
                style={{ "transform": "rotate(90deg)" }}
              />
            </div>
          </Typography>
        </Button>
      </div>
    </div>
  );
};

export default Specs;
